import React, { useState, useCallback } from 'react'
import { AppDialog } from './AppDialog'
import { Button } from './Button'
import { useNls } from '../../nls/nlsProvider'
import { recordConsent, getConsentNoticeDetails } from '../../services/consentService'
import type { UserProfile } from '../../types/schema'

export interface ConsentNoticeDialogProps {
  userProfile?: Partial<UserProfile> | null
  uid?: string | null
  onConsentRecorded?: () => void
}

export const ConsentNoticeDialog: React.FC<ConsentNoticeDialogProps> = ({
  userProfile,
  uid,
  onConsentRecorded,
}) => {
  const { t } = useNls()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const details = getConsentNoticeDetails(userProfile)

  const handleAgree = useCallback(async () => {
    if (!uid) return
    setSaving(true)
    setError(null)
    try {
      await recordConsent(uid)
      onConsentRecorded?.()
    } catch (err) {
      console.warn('[ConsentNoticeDialog] Failed to record updated consent', err)
      setError(t('NLSConsentNoticeError'))
    } finally {
      setSaving(false)
    }
  }, [uid, onConsentRecorded, t])

  if (!details.needsNotice || !uid) {
    return null
  }

  const message =
    details.termsChanged && details.privacyChanged
      ? t('NLSConsentNoticeBodyBoth')
      : details.termsChanged
        ? t('NLSConsentNoticeBodyTerms')
        : t('NLSConsentNoticeBodyPrivacy')

  return (
    <AppDialog
      open={true}
      title={t('NLSConsentNoticeTitle')}
      message={message}
      showClose={false}
      data-testid="consent-notice-dialog"
      actions={[
        {
          label: t('NLSConsentNoticeActionAgree'),
          variant: 'primary',
          loading: saving,
          onClick: handleAgree,
        },
      ]}
    >
      <div className="consentNoticeContent" style={{ marginTop: '0.75rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
          {details.termsChanged && (
            <p style={{ margin: 0 }}>
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--primary)', textDecoration: 'underline' }}
              >
                {t('NLSPublicLinkTerms')}
              </a>
            </p>
          )}
          {details.privacyChanged && (
            <p style={{ margin: 0 }}>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--primary)', textDecoration: 'underline' }}
              >
                {t('NLSPublicLinkPrivacy')}
              </a>
            </p>
          )}
        </div>
        {error && (
          <div
            role="alert"
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'color-mix(in srgb, var(--danger) 15%, transparent)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
            }}
          >
            <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>{error}</span>
            <Button variant="secondary" size="sm" onClick={handleAgree} loading={saving}>
              {t('NLSConsentNoticeActionRetry')}
            </Button>
          </div>
        )}
      </div>
    </AppDialog>
  )
}
